import { useEffect, useRef, type ReactNode } from 'react';
import { useNotificationStore, type Notification as StoreNotification, type NotificationType } from '@repletoryreach/sdk-utils/store';
import { Portal } from '../../core/Portal/Portal.js';
import { Notification, type NotificationVariant } from '../Notification/Notification.js';
import './NotificationsProvider.css';

export type NotificationsPosition =
  | 'top-left' | 'top' | 'top-right'
  | 'bottom-left' | 'bottom' | 'bottom-right';

export interface NotificationsProviderProps {
  children?: ReactNode;
  /** Where the notifications stack renders. Default `bottom-right`. */
  position?: NotificationsPosition;
  /** Maximum visible at once (older queued, FIFO). Default 5. */
  limit?: number;
  /** Gap between cards in px. Default 8. */
  gap?: number;
  /** Default visual style for every notification. */
  defaultVariant?: NotificationVariant;
  /** Render into a different portal target. */
  portalTarget?: HTMLElement | string;
  /** Width of each notification card. */
  width?: number | string;
  /** Z-index override; defaults to `--rr-z-toast`. */
  zIndex?: number;
  /** Provide icons per notification type. */
  icons?: Partial<Record<NotificationType, ReactNode>>;
}

const TYPE_TO_ICON: Record<NotificationType, ReactNode> = {
  success: <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 9l4 4 8-9" /></svg>,
  error:   <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="9" cy="9" r="7" /><path d="M9 5v5M9 13.5v.01" /></svg>,
  warning: <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 2l8 14H1L9 2z" /><path d="M9 7v4M9 13.5v.01" /></svg>,
  info:    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="9" cy="9" r="7" /><path d="M9 12V8M9 5.5v.01" /></svg>
};

/**
 * `<NotificationsProvider>`: mounts a portal that renders the queue from
 * sdk-utils `useNotificationStore`. App code calls
 * `notifySuccess/Error/Info/Warning` (from `@repletoryreach/sdk-utils/store`)
 * or this package's `show()` to push.
 *
 *   <NotificationsProvider position="bottom-right" defaultVariant="glass">
 *     <App />
 *   </NotificationsProvider>
 *
 *   import { notifySuccess } from '@repletoryreach/sdk-utils/store';
 *   notifySuccess('Saved');
 */
export function NotificationsProvider({
  children,
  position = 'bottom-right',
  limit = 5,
  gap = 8,
  defaultVariant = 'default',
  portalTarget,
  width = 360,
  zIndex,
  icons
}: NotificationsProviderProps): ReactNode {
  const items = useNotificationStore((s) => s.notifications);
  const dismiss = useNotificationStore((s) => s.dismiss);

  // Auto-dismiss timers respect each notification's `durationMs`.
  const timers = useRef<Map<string, number>>(new Map());
  useEffect(() => {
    const t = timers.current;
    items.forEach((n) => {
      if (t.has(n.id)) return;
      if (n.durationMs && n.durationMs > 0) {
        const handle = window.setTimeout(() => {
          dismiss(n.id);
          t.delete(n.id);
        }, n.durationMs);
        t.set(n.id, handle);
      }
    });
    for (const [id, handle] of t.entries()) {
      if (!items.find((x) => x.id === id)) {
        clearTimeout(handle);
        t.delete(id);
      }
    }
  }, [items, dismiss]);

  useEffect(() => () => {
    for (const handle of timers.current.values()) clearTimeout(handle);
    timers.current.clear();
  }, []);

  const visible = items.slice(0, limit);
  const resolvedIcons: Record<NotificationType, ReactNode> = { ...TYPE_TO_ICON, ...(icons ?? {}) };

  return (
    <>
      {children}
      <Portal target={portalTarget}>
        <div
          className={`rr-notifications-stack rr-notifications-stack--${position}`}
          style={{
            gap,
            width: typeof width === 'number' ? `${width}px` : width,
            zIndex: zIndex ?? 'var(--rr-z-toast)' as unknown as number
          }}
          role="region"
          aria-label="Notifications"
        >
          {visible.map((n: StoreNotification) => (
            <Notification
              key={n.id}
              title={n.title}
              color={n.type}
              icon={resolvedIcons[n.type]}
              variant={defaultVariant}
              onClose={() => dismiss(n.id)}
            >
              {n.message}
              {n.action ? (
                <div style={{ marginTop: 'var(--rr-spacing-xs)' }}>
                  <button
                    type="button"
                    className="rr-notification__action"
                    onClick={() => { n.action!.onClick(); dismiss(n.id); }}
                  >
                    {n.action.label}
                  </button>
                </div>
              ) : null}
            </Notification>
          ))}
        </div>
      </Portal>
    </>
  );
}
